import * as THREE from 'three/webgpu';
import { PointsMaterialFactory } from '../../../../material/PointsMaterialFactory';
import type { SketchGroup } from '../../../../mesh/sketch/SketchGroup';
import { SketchArcObject, type SketchArcDefinition } from '../../../../mesh/sketch/SketchArcObject';
import { SketchCircleObject } from '../../../../mesh/sketch/SketchCircleObject';
import {
	SketchPointKind,
} from '../../../../mesh/sketch/ISketchObject';
import { SketchEllipseObject, type SketchEllipseDefinition } from '../../../../mesh/sketch/SketchEllipseObject';
import { SketchLineObject } from '../../../../mesh/sketch/SketchLineObject';
import { SketchParabolaObject } from '../../../../mesh/sketch/SketchParabolaObject';
import { SketchPointObject } from '../../../../mesh/sketch/SketchPointObject';
import { SketchSplineObject } from '../../../../mesh/sketch/SketchSplineObject';
import { SketchStyleSplineObject } from '../../../../mesh/sketch/SketchStyleSplineObject';
import { SKETCH_GEOMETRY_TOLERANCE_CONFIG } from '../../../../sketch/config/SketchGeometryToleranceConfig';
import type {
	BuildGeometryReferencePreviewBatchServiceDependencies,
	BuildGeometryReferencePreviewBatchServiceInput,
	PreviewEllipseGeometryPayload,
} from '../../../../../types/reference/sketch-entity-reference/BuildGeometryReferencePreviewBatchService';
import type {
	SketchEntityReferencePreviewBatchResult,
	SketchEntityReferencePreviewObject,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferencePreviewCoordinator';
import type { ProjectionResult } from '../../model/ProjectionResult';
import { markSketchEntityReferencePreview } from './SketchEntityReferencePreviewMarker';
import { SketchEntityReferencePreviewRetirementService } from './SketchEntityReferencePreviewRetirementService';

export type {
	BuildGeometryReferencePreviewBatchServiceDependencies,
	BuildGeometryReferencePreviewBatchServiceInput,
	PreviewEllipseGeometryPayload,
} from '../../../../../types/reference/sketch-entity-reference/BuildGeometryReferencePreviewBatchService';

/**
 * 预览批次构建服务。
 * 说明：
 * 1. 该服务只消费 `ProjectionResult[]`，不接触 legacy projection map；
 * 2. 按 `projectionResultKind` 直接构造当前分支已有的草图对象；
	 * 3. 构建完成后显式返回根实体与完整对象所有权清单；
	 * 4. 激活回调统一完成场景挂载和 Preview 标记；
	 * 5. 这样 preview coordinator 可以先做 revision 校验，再决定是否真的激活本批对象；
	 * 6. 该服务不再关心来源是 sketch-entity、feature-edge 还是 topology-edge。
 */
export class BuildGeometryReferencePreviewBatchService {
	/** 构建异常时用于回滚已取得所有权对象的退役端口。 */
	private readonly dependencies: BuildGeometryReferencePreviewBatchServiceDependencies;

	/**
	 * 创建 Preview 批次构建服务。
	 *
	 * @param dependencies 构建异常回滚能力。
	 */
	public constructor(dependencies?: BuildGeometryReferencePreviewBatchServiceDependencies) {
		if (dependencies) {
			this.dependencies = dependencies;
			return;
		}

		const retirementService = new SketchEntityReferencePreviewRetirementService();
		this.dependencies = {
			retirePreviewObjects: (previewObjects) => (
				retirementService.retirePreviewObjects(previewObjects)
			),
		};
	}

	/**
	 * 构建尚未激活的预览批次。
	 *
	 * @param input 批次构建输入。
	 * @returns 与当前 preview coordinator 契约兼容的批次结果。
	 */
	public build(
		input: BuildGeometryReferencePreviewBatchServiceInput,
	): SketchEntityReferencePreviewBatchResult {
		// 先校验整个批次的可构建条件，避免后续项失败时遗留前序项已经分配的 Preview 资源。
		this.assertProjectionResultsBuildable(input.projectionResults, input.targetSketch);
		const createdEntities: SketchEntityReferencePreviewObject[] = [];
		try {
			input.projectionResults.forEach((projectionResult) => {
				switch (projectionResult.projectionResultKind) {
				case 'line':
					createdEntities.push(this.buildLinePreviewObject(
						input.targetSketch,
						projectionResult.geometryPayload,
					));
					return;
				case 'circle':
					createdEntities.push(this.buildCirclePreviewObject(
						input.targetSketch,
						projectionResult.geometryPayload,
					));
					return;
				case 'arc':
					createdEntities.push(this.buildArcPreviewObject(
						input.targetSketch,
						projectionResult.geometryPayload,
					));
					return;
				case 'ellipse':
					createdEntities.push(this.buildEllipsePreviewObject(
						input.targetSketch,
						{
							...projectionResult.geometryPayload,
							startAngle: 0,
							endAngle: Math.PI * 2,
							clockwise: false,
						},
						false,
					));
					return;
				case 'ellipse-arc':
					createdEntities.push(this.buildEllipsePreviewObject(
						input.targetSketch,
						projectionResult.geometryPayload,
						true,
					));
					return;
				case 'parabola':
					createdEntities.push(this.buildParabolaPreviewObject(
						input.targetSketch,
						projectionResult.geometryPayload,
					));
					return;
				case 'spline':
					createdEntities.push(this.buildSplinePreviewObject(
						input.targetSketch,
						projectionResult.geometryPayload,
					));
					return;
				case 'style-spline':
					createdEntities.push(this.buildStyleSplinePreviewObject(
						input.targetSketch,
						projectionResult.geometryPayload,
					));
					return;
				}
			});

			this.applyPreviewVisibilityPolicy(createdEntities);
			const previewObjects = this.collectPreviewObjects(createdEntities);

			return {
				createdEntities,
				previewObjects,
				diagnostics: [],
				activatePreviewEntities: () => {
					createdEntities.forEach((entity) => {
						this.attachPreviewObjectsToTargetSketch(input.targetSketch, entity);
					});
					previewObjects.forEach((previewObject) => {
						markSketchEntityReferencePreview(previewObject);
					});
				},
			};
		} catch (error) {
			const ownedPreviewObjects = this.collectPreviewObjects(createdEntities);
			this.dependencies.retirePreviewObjects(ownedPreviewObjects);
			throw error;
		}
	}

	/**
	 * 在分配任何 Preview 对象前校验整批投影结果的构建前提。
	 *
	 * @param projectionResults 已归一化的投影结果。
	 * @param targetSketch Preview 所属目标草图。
	 */
	private assertProjectionResultsBuildable(
		projectionResults: readonly ProjectionResult[],
		targetSketch: SketchGroup,
	): void {
		projectionResults.forEach((projectionResult) => {
			switch (projectionResult.projectionResultKind) {
				case 'line':
					this.toWorldVector3(projectionResult.geometryPayload.startWorld);
					this.toWorldVector3(projectionResult.geometryPayload.endWorld);
					return;
				case 'circle':
				case 'arc':
				case 'ellipse':
				case 'ellipse-arc':
					this.toWorldVector3(projectionResult.geometryPayload.centerWorld);
					return;
				case 'parabola': {
					const focalWorld = this.toWorldVector3(projectionResult.geometryPayload.focalWorld);
					const apexWorld = this.toWorldVector3(projectionResult.geometryPayload.apexWorld);
					const normal = targetSketch.referencePlane.normal.clone().normalize();
					const planarAxis = focalWorld.sub(apexWorld);
					planarAxis.sub(normal.multiplyScalar(planarAxis.dot(normal)));
					if (planarAxis.length() < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
						throw new Error('抛物线定义无效：焦点与顶点不能重合。');
					}
					return;
				}
				case 'spline':
				case 'style-spline': {
					const isStyleSpline = projectionResult.projectionResultKind === 'style-spline';
					if (projectionResult.geometryPayload.controlPointWorlds.length < 2) {
						throw new Error(isStyleSpline
							? '样式样条预览构建失败：控制点数量不足。'
							: '普通样条预览构建失败：控制点数量不足。');
					}
					if (projectionResult.geometryPayload.curvePointWorlds.length < 2) {
						throw new Error(isStyleSpline
							? '样式样条预览构建失败：曲线采样点数量不足。'
							: '样条曲线缺少初始采样点，无法构造几何。');
					}
					for (const point of projectionResult.geometryPayload.controlPointWorlds) {
						this.toWorldVector3(point);
					}
					for (const point of projectionResult.geometryPayload.curvePointWorlds) {
						this.toWorldVector3(point);
					}
					return;
				}
			}
		});
	}

	/**
	 * 收集一个 Preview 批次拥有的全部对象。
	 *
	 * 说明：
	 * 1. 草图线、圆弧等对象的控制点可能直接挂到目标草图，而不是根实体子树；
	 * 2. BatchService 创建了这些对象，因此由这里显式形成完整所有权清单；
	 * 3. Coordinator 与 RetirementService 只消费清单，不再猜测具体草图对象协议。
	 *
	 * @param createdEntities 当前批次的 Preview 根实体。
	 * @returns 按 Object3D 引用去重的完整对象清单。
	 */
	private collectPreviewObjects(
		createdEntities: readonly SketchEntityReferencePreviewObject[],
	): THREE.Object3D[] {
		const previewObjects = new Set<THREE.Object3D>();

		const visit = (previewObject: THREE.Object3D): void => {
			if (previewObjects.has(previewObject)) {
				return;
			}

			previewObjects.add(previewObject);
			previewObject.children.forEach(visit);

			const relatedObject = previewObject as SketchEntityReferencePreviewObject;
			if (relatedObject.startPoint) {
				visit(relatedObject.startPoint);
			}
			if (relatedObject.endPoint) {
				visit(relatedObject.endPoint);
			}
			relatedObject.getRelatedSketchPointsSnapshot?.().forEach(visit);
		};

		createdEntities.forEach(visit);
		return [...previewObjects];
	}

	/**
	 * 构造预览线对象。
	 *
	 * @param targetSketch 目标草图。
	 * @param geometryPayload 线几何载荷。
	 * @returns 预览线对象。
	 */
	private buildLinePreviewObject(
		targetSketch: SketchGroup,
		geometryPayload: Extract<ProjectionResult, { projectionResultKind: 'line' }>['geometryPayload'],
	): SketchLineObject {
		const startPoint = this.createControlPoint(geometryPayload.startWorld, targetSketch);
		const endPoint = this.createControlPoint(geometryPayload.endWorld, targetSketch);
		return new SketchLineObject({
			referencePlane: targetSketch.referencePlane,
			startPoint,
			endPoint,
			isDrawingPreviewLine: true,
		});
	}

	/**
	 * 构造预览圆对象。
	 *
	 * @param targetSketch 目标草图。
	 * @param geometryPayload 圆几何载荷。
	 * @returns 预览圆对象。
	 */
	private buildCirclePreviewObject(
		targetSketch: SketchGroup,
		geometryPayload: Extract<ProjectionResult, { projectionResultKind: 'circle' }>['geometryPayload'],
	): SketchCircleObject {
		const circle = new SketchCircleObject({
			center: this.toWorldVector3(geometryPayload.centerWorld),
			radius: geometryPayload.radius,
			referencePlane: targetSketch.referencePlane,
		});
		circle.updateGeometry();
		this.markPointAsPreviewCaptureIgnored(circle.getCenterControlPoint());
		this.markPointAsPreviewCaptureIgnored(circle.getEdgeControlPoint());
		circle.getQuadrantPoints().forEach((point) => {
			this.markPointAsPreviewCaptureIgnored(point);
		});
		return circle;
	}

	/**
	 * 构造预览圆弧对象。
	 *
	 * @param targetSketch 目标草图。
	 * @param geometryPayload 圆弧几何载荷。
	 * @returns 预览圆弧对象。
	 */
	private buildArcPreviewObject(
		targetSketch: SketchGroup,
		geometryPayload: Extract<ProjectionResult, { projectionResultKind: 'arc' }>['geometryPayload'],
	): SketchArcObject {
		const definition: SketchArcDefinition = {
			centerWorld: this.toWorldVector3(geometryPayload.centerWorld),
			radius: geometryPayload.radius,
			startAngle: geometryPayload.startAngle,
			endAngle: geometryPayload.endAngle,
			clockwise: geometryPayload.clockwise,
		};
		const startPoint = this.createControlPoint(
			this.resolveCircularWorldPoint(definition.centerWorld, definition.radius, definition.startAngle, targetSketch),
			targetSketch,
		);
		const endPoint = this.createControlPoint(
			this.resolveCircularWorldPoint(definition.centerWorld, definition.radius, definition.endAngle, targetSketch),
			targetSketch,
		);
		const centerPoint = this.createControlPoint(definition.centerWorld, targetSketch);
		const arc = new SketchArcObject({
			referencePlane: targetSketch.referencePlane,
			definition,
			startPoint,
			endPoint,
			centerPoint,
			isDrawingPreviewArc: true,
		});
		arc.updateArc();
		return arc;
	}

	/**
	 * 构造预览椭圆或椭圆弧对象。
	 *
	 * @param targetSketch 目标草图。
	 * @param geometryPayload 椭圆几何载荷。
	 * @returns 预览椭圆对象。
	 */
	private buildEllipsePreviewObject(
		targetSketch: SketchGroup,
		geometryPayload: PreviewEllipseGeometryPayload,
		useArcEndpoints: boolean,
	): SketchEllipseObject {
		const definition: SketchEllipseDefinition = {
			centerWorld: this.toWorldVector3(geometryPayload.centerWorld),
			radiusX: geometryPayload.radiusX,
			radiusY: geometryPayload.radiusY,
			rotation: geometryPayload.rotation,
			startAngle: geometryPayload.startAngle,
			endAngle: geometryPayload.endAngle,
			clockwise: geometryPayload.clockwise,
		};
		const startPoint = this.createControlPoint(useArcEndpoints
			? this.resolveEllipseWorldPointByParameter(definition, definition.startAngle, targetSketch)
			: this.resolveEllipseAxisWorldPoint(
				definition.centerWorld,
				definition.radiusX,
				definition.rotation,
				targetSketch,
			), targetSketch);
		const endPoint = this.createControlPoint(useArcEndpoints
			? this.resolveEllipseWorldPointByParameter(definition, definition.endAngle, targetSketch)
			: this.resolveEllipseAxisWorldPoint(
				definition.centerWorld,
				definition.radiusY,
				definition.rotation + Math.PI * 0.5,
				targetSketch,
			), targetSketch);
		const centerPoint = this.createControlPoint(definition.centerWorld, targetSketch);
		const ellipse = new SketchEllipseObject({
			referencePlane: targetSketch.referencePlane,
			definition,
			startPoint,
			endPoint,
			centerPoint,
		});
		ellipse.updateEllipse();
		this.markPointAsPreviewCaptureIgnored(ellipse.getCenterPoint());
		this.markPointAsPreviewCaptureIgnored(ellipse.getStartPoint());
		this.markPointAsPreviewCaptureIgnored(ellipse.getEndPoint());
		ellipse.getQuadrantPoints().forEach((point) => {
			this.markPointAsPreviewCaptureIgnored(point);
		});
		return ellipse;
	}

	/**
	 * 构造预览抛物线对象。
	 *
	 * @param targetSketch 目标草图。
	 * @param geometryPayload 抛物线几何载荷。
	 * @returns 预览抛物线对象。
	 */
	private buildParabolaPreviewObject(
		targetSketch: SketchGroup,
		geometryPayload: Extract<ProjectionResult, { projectionResultKind: 'parabola' }>['geometryPayload'],
	): SketchParabolaObject {
		const parabola = new SketchParabolaObject({
			referencePlane: targetSketch.referencePlane,
			definition: {
				focalWorld: this.toWorldVector3(geometryPayload.focalWorld),
				apexWorld: this.toWorldVector3(geometryPayload.apexWorld),
				startParameter: geometryPayload.startParameter,
				endParameter: geometryPayload.endParameter,
				segments: geometryPayload.segments,
			},
		});
		parabola.getControlPoints().forEach((point) => {
			this.markPointAsPreviewCaptureIgnored(point);
		});
		return parabola;
	}

	/**
	 * 构造预览普通样条对象。
	 *
	 * @param targetSketch 目标草图。
	 * @param geometryPayload 样条几何载荷。
	 * @returns 预览普通样条对象。
	 */
	private buildSplinePreviewObject(
		targetSketch: SketchGroup,
		geometryPayload: Extract<ProjectionResult, { projectionResultKind: 'spline' }>['geometryPayload'],
	): SketchSplineObject {
		const controlPoints = geometryPayload.controlPointWorlds
			.map((position) => this.createControlPoint(position, targetSketch));
		const startPoint = controlPoints[0];
		const endPoint = controlPoints[controlPoints.length - 1];
		if (!startPoint || !endPoint || controlPoints.length < 2) {
			throw new Error('普通样条预览构建失败：控制点数量不足。');
		}

		return new SketchSplineObject({
			referencePlane: targetSketch.referencePlane,
			startPoint,
			endPoint,
			controlPoints: controlPoints.slice(1, -1),
			curvePoints: geometryPayload.curvePointWorlds.map((point) => this.toWorldVector3(point)),
			curveMetadata: geometryPayload.curveMetadata,
		});
	}

	/**
	 * 构造预览样式样条对象。
	 *
	 * @param targetSketch 目标草图。
	 * @param geometryPayload 样式样条几何载荷。
	 * @returns 预览样式样条对象。
	 */
	private buildStyleSplinePreviewObject(
		targetSketch: SketchGroup,
		geometryPayload: Extract<ProjectionResult, { projectionResultKind: 'style-spline' }>['geometryPayload'],
	): SketchStyleSplineObject {
		const controlPoints = geometryPayload.controlPointWorlds
			.map((position) => this.createControlPoint(position, targetSketch));
		const startPoint = controlPoints[0];
		const endPoint = controlPoints[controlPoints.length - 1];
		if (!startPoint || !endPoint || controlPoints.length < 2) {
			throw new Error('样式样条预览构建失败：控制点数量不足。');
		}

		return new SketchStyleSplineObject({
			referencePlane: targetSketch.referencePlane,
			startPoint,
			endPoint,
			controlPoints: controlPoints.slice(1, -1),
			curvePoints: geometryPayload.curvePointWorlds.map((point) => this.toWorldVector3(point)),
			curveMetadata: geometryPayload.curveMetadata,
		});
	}

	/**
	 * 将 preview 根对象与必要辅助点挂到目标草图。
	 * 说明：
	 * 1. 预览对象若不进入 `targetSketch` 场景树，后续 UI 接上后不会可见；
	 * 2. 这里按当前图元真实需要挂载最小辅助点集合，对齐 temp 分支生命周期模型；
	 * 3. `SketchEntityReferencePreviewCoordinator.clearPreview()` 会统一回收这些对象，因此这里不额外持有引用。
	 *
	 * @param targetSketch 目标草图。
	 * @param previewObject preview 根对象。
	 */
	private attachPreviewObjectsToTargetSketch(
		targetSketch: SketchGroup,
		previewObject: SketchEntityReferencePreviewObject,
	): void {
		targetSketch.add(previewObject);

		if (previewObject instanceof SketchLineObject) {
			targetSketch.add(previewObject.startPoint);
			targetSketch.add(previewObject.endPoint);
			return;
		}

		if (previewObject instanceof SketchArcObject) {
			targetSketch.add(previewObject.getStartPoint());
			targetSketch.add(previewObject.getEndPoint());
			targetSketch.add(previewObject.getCenterPoint());
			return;
		}

		if (previewObject instanceof SketchCircleObject) {
			targetSketch.add(previewObject.getCenterControlPoint());
			return;
		}

		if (previewObject instanceof SketchEllipseObject) {
			targetSketch.add(previewObject.getCenterPoint());
			targetSketch.add(previewObject.getStartPoint());
			targetSketch.add(previewObject.getEndPoint());
			return;
		}

		if (previewObject instanceof SketchParabolaObject) {
			previewObject.getControlPoints().forEach((point) => {
				targetSketch.add(point);
			});
			return;
		}

		if (previewObject instanceof SketchSplineObject || previewObject instanceof SketchStyleSplineObject) {
			return;
		}
	}

	/**
	 * 统一创建 preview 控制点。
	 *
	 * @param position 世界坐标。
	 * @param targetSketch 目标草图。
	 * @returns 草图控制点对象。
	 */
	private createControlPoint(
		position: import('../../model/ProjectionResult').WorldCoordinate | { x: number; y: number; z: number },
		targetSketch: SketchGroup,
	): SketchPointObject {
		const worldPosition = this.toWorldVector3(position);
		const point = new SketchPointObject({
			position: worldPosition,
			pointKind: SketchPointKind.ControlPoint,
			material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
			referencePlane: targetSketch.referencePlane,
		});
		this.markPointAsPreviewCaptureIgnored(point);
		return point;
	}

	/**
	 * 给预览控制点写入现有绘制态忽略标记。
	 * 说明：
	 * 1. 当前部分点捕捉策略仍会直接扫描草图组维护的点集合；
	 * 2. Convert Entities preview 已有独立 marker，但这些直扫路径暂时只识别 `isDrawingPreviewPoint`；
	 * 3. 因此这里补一层兼容标记，保证已挂载到 `targetSketch` 的辅助点不会重新进入点捕捉。
	 *
	 * @param point 需要排除捕捉的预览点对象。
	 */
	private markPointAsPreviewCaptureIgnored(point: SketchPointObject): void {
		point.isDrawingPreviewPoint = true;
	}

	/**
	 * 计算目标草图平面上的圆参数点世界坐标。
	 *
	 * @param centerWorld 圆心世界坐标。
	 * @param radius 半径。
	 * @param angle 参数角。
	 * @param targetSketch 目标草图。
	 * @returns 世界坐标点。
	 */
	private resolveCircularWorldPoint(
		centerWorld: THREE.Vector3,
		radius: number,
		angle: number,
		targetSketch: SketchGroup,
	): THREE.Vector3 {
		const localCenter = targetSketch.referencePlane.worldToLocal(centerWorld);
		return targetSketch.referencePlane.localToWorld(
			localCenter.x + Math.cos(angle) * radius,
			localCenter.y + Math.sin(angle) * radius,
		);
	}

	/**
	 * 计算目标草图平面上的椭圆轴端点世界坐标。
	 *
	 * @param centerWorld 椭圆中心。
	 * @param axisRadius 轴向半径。
	 * @param axisAngle 轴向角。
	 * @param targetSketch 目标草图。
	 * @returns 世界坐标点。
	 */
	private resolveEllipseAxisWorldPoint(
		centerWorld: THREE.Vector3,
		axisRadius: number,
		axisAngle: number,
		targetSketch: SketchGroup,
	): THREE.Vector3 {
		const localCenter = targetSketch.referencePlane.worldToLocal(centerWorld);
		return targetSketch.referencePlane.localToWorld(
			localCenter.x + Math.cos(axisAngle) * axisRadius,
			localCenter.y + Math.sin(axisAngle) * axisRadius,
		);
	}

	/**
	 * 计算目标草图平面上的椭圆参数点世界坐标。
	 *
	 * @param definition 椭圆定义。
	 * @param parameter 椭圆参数角。
	 * @param targetSketch 目标草图。
	 * @returns 世界坐标点。
	 */
	private resolveEllipseWorldPointByParameter(
		definition: SketchEllipseDefinition,
		parameter: number,
		targetSketch: SketchGroup,
	): THREE.Vector3 {
		const localCenter = targetSketch.referencePlane.worldToLocal(definition.centerWorld);
		const localPoint = new THREE.Vector2(
			localCenter.x + definition.radiusX * Math.cos(parameter),
			localCenter.y + definition.radiusY * Math.sin(parameter),
		).rotateAround(localCenter, definition.rotation);
		return targetSketch.referencePlane.localToWorld(localPoint);
	}

	/**
	 * 将输入位置统一复制为当前文件使用的 `three/webgpu` Vector3。
	 * 说明：
	 * 1. 当前仓库会同时混用 `three` 与 `three/webgpu` 的 Vector3；
	 * 2. 不能再依赖 `instanceof THREE.Vector3`，否则跨入口对象会误判；
	 * 3. 这里统一走结构化复制，只读取 `x/y/z` 数值字段或固定长度元组。
	 *
	 * @param position 位置输入。
	 * @returns 复制后的 `Vector3`。
	 */
	private toWorldVector3(
		position: import('../../model/ProjectionResult').WorldCoordinate | { x: number; y: number; z: number },
	): THREE.Vector3 {
		if (
			Array.isArray(position)
			&& position.length >= 3
			&& [0, 1, 2].every((coordinateIndex) => (
				typeof position[coordinateIndex] === 'number'
				&& Number.isFinite(position[coordinateIndex])
			))
		) {
			return new THREE.Vector3(position[0], position[1], position[2]);
		}

		if (this.isVector3Like(position)) {
			return new THREE.Vector3(position.x, position.y, position.z);
		}

		throw new Error('预览控制点位置非法，缺少有效的 x/y/z 数值字段。');
	}

	/**
	 * 判断对象是否满足 Vector3-like 结构。
	 *
	 * @param value 待判断值。
	 * @returns 命中 `x/y/z` 数值结构时返回 true。
	 */
	private isVector3Like(value: unknown): value is { x: number; y: number; z: number } {
		if (!value || typeof value !== 'object') {
			return false;
		}

		return Number.isFinite(Reflect.get(value, 'x'))
			&& Number.isFinite(Reflect.get(value, 'y'))
			&& Number.isFinite(Reflect.get(value, 'z'));
	}

	/**
	 * 应用 preview 可视化策略。
	 * 说明：
	 * 1. 圆预览延续 temp worktree 的视觉策略，只保留圆心提示；
	 * 2. 其他图元保持当前对象默认显示状态，避免额外引入新视觉约定。
	 *
	 * @param createdEntities 已创建的 preview 对象。
	 */
	private applyPreviewVisibilityPolicy(
		createdEntities: SketchEntityReferencePreviewObject[],
	): void {
		createdEntities.forEach((entity) => {
			if (entity instanceof SketchCircleObject) {
				entity.getEdgeControlPoint().visible = false;
			}
		});
	}
}
